import ServiceWrapper, { type ServiceOptions } from "@utils/ServiceWrapper";
import {
    BUS_PASS_APPLICATION_BY_STUDENT_NUMBER,
    CHECKIN_WINDOW,
    CHECKIN_WINDOW_DETAIL,
    CHECKIN_WINDOW_PARENT,
    CUSTOM_ATTRIBUTE_GET,
    ECHECKIN_BUS_PASS_APPLICATIONS_CREATE,
    ELECTRONIC_SIGNATURE_BY_MAPID_AND_TYPE,
    GUARDIAN_STUDENT_MAP,
    HIST_BUS_PASS_APPLICATION_BY_STUDENT_NUMBER,
    SELECTED_VALUE
} from "@const/EcheckinConst";

type NullableKey = number | string | null;

interface EcheckinDaoOptions {
    action: string;
    dto?: unknown;
    locKey?: NullableKey;
    mapId?: NullableKey;
    params?: unknown;
    schoolYearKey?: NullableKey;
    studentNumber?: NullableKey;
    type?: string | null;
    username?: string | null;
    signal?: AbortSignal;
}

/**
 * Data Access Object for the eCheck-In Service.
 */
const EcheckinDao = <T = unknown>(options: EcheckinDaoOptions): Promise<{ data: T }> | null => {
    const { action, dto, locKey, mapId, params, schoolYearKey, studentNumber, type, username, signal } = options;

    const requestOptions: ServiceOptions = { url: "", method: "GET" };

    const actionConfig: Record<string, Partial<ServiceOptions>> = {
        busPassApplicationCreate: { data: dto, method: "POST", url: ECHECKIN_BUS_PASS_APPLICATIONS_CREATE },
        busPassApplicationsByStudentNumber: {
            method: "GET",
            url: `${BUS_PASS_APPLICATION_BY_STUDENT_NUMBER}/${studentNumber}/${schoolYearKey}/index.json`
        },
        histBusPassApplicationsByStudentNumber: {
            method: "GET",
            url: `${HIST_BUS_PASS_APPLICATION_BY_STUDENT_NUMBER}/${studentNumber}/index.json`
        },
        checkinWindowParentCheckin: {
            method: "GET",
            params: params ?? undefined,
            url: `${CHECKIN_WINDOW_PARENT}/${schoolYearKey}/${locKey}/detail.json`
        },
        checkinWindowBySchoolYearAndType: {
            method: "GET",
            params,
            url: `${CHECKIN_WINDOW}/${schoolYearKey}/index.json`
        },
        checkinWindowTransportation: {
            method: "GET",
            params,
            url: `${CHECKIN_WINDOW_DETAIL}/${schoolYearKey}/TRANSPORTATION/detail.json`
        },
        customStudentAttributeRead: {
            method: "GET",
            params,
            url: `${CUSTOM_ATTRIBUTE_GET}/${studentNumber}/detail.json`
        },
        eSignatureRead: {
            method: "GET",
            url: `${ELECTRONIC_SIGNATURE_BY_MAPID_AND_TYPE}/${mapId}/${type}/detail.json`
        },
        guardianStudentMapByStudentNumberRead: {
            method: "GET",
            params,
            url: `${GUARDIAN_STUDENT_MAP}/${studentNumber}/${username}/detail.json`
        },
        guardianStudentMapRead: { method: "GET", url: `${GUARDIAN_STUDENT_MAP}/${mapId}/detail.json` },
        selectedValuesRead: { method: "GET", params, url: SELECTED_VALUE },
        selectedValue: { data: dto, method: "POST", url: SELECTED_VALUE }
    };

    const config = actionConfig[action];
    if (!config) {
        const error = new Error(`EcheckinDao: Invalid action "${action}" provided`);
        console.error("EcheckinDao: Invalid action provided");
        throw error;
    }

    return ServiceWrapper.serviceCall({
        options: { ...requestOptions, ...config, signal }
    }) as Promise<{ data: T }>;
};

export default EcheckinDao;
